import * as d3 from 'd3';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ObjectiveType, ObjectiveTypeScope } from 'utils/models/Objective';
import { getContrastColor } from 'utils/util/color';

const SCOPE_LEVEL: Record<ObjectiveTypeScope, number> = {
  strategic: 0,
  individual: 1,
};

interface Props {
  objectiveTypes: ObjectiveType[];
  onNodeClick?: (id: number) => void;
}

interface GraphNode {
  id: number;
  name: string;
  color: string;
  scope: ObjectiveTypeScope;
  level: number;
  levelIndex: number;
  x: number;
  y: number;
}

interface GraphLink {
  sourceId: number;
  targetId: number;
  sourceColor: string;
}

const NODE_WIDTH = 180;
const MIN_NODE_WIDTH = 70;
const NODE_HEIGHT = 30;
const NODE_HORIZONTAL_SPACING = 40;
const LEVEL_VERTICAL_SPACING = 80;
const SELF_LINK_RADIUS = 18;
const PADDING_X = 20;
const PADDING_Y = 20;
// Same-level detour arc: base clearance above/below the row plus an extra
// step per node skipped, so longer-span detours don't overlap shorter ones.
const DETOUR_BASE_OFFSET = 12;
const DETOUR_STEP_OFFSET = 16;

const ObjectiveTypeLinkGraph: FC<Props> = ({ objectiveTypes, onNodeClick }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgWidth, setSvgWidth] = useState(0);

  const handleResize = useCallback(() => {
    if (svgRef.current) {
      setSvgWidth(svgRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (!svgRef.current || objectiveTypes.length < 2) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Group types by scope level
    const levelGroups = new Map<number, ObjectiveType[]>();
    for (const t of objectiveTypes) {
      const level = SCOPE_LEVEL[t.scope] ?? 3;
      if (!levelGroups.has(level)) levelGroups.set(level, []);
      levelGroups.get(level)!.push(t);
    }

    // Sort levels: lowest number (highest rank) first — these go to the top
    const activeLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);
    const width = svgRef.current.clientWidth || 800;

    // Shrink node width to fit when the widest level would overflow the
    // container, clamped to a minimum that still fits a few characters.
    const maxNodesAtLevel = Math.max(
      ...Array.from(levelGroups.values()).map((g) => g.length)
    );
    const availableWidth = Math.max(0, width - 2 * PADDING_X);
    const defaultGroupWidth =
      maxNodesAtLevel * NODE_WIDTH +
      (maxNodesAtLevel - 1) * NODE_HORIZONTAL_SPACING;
    const nodeWidth =
      defaultGroupWidth > availableWidth
        ? Math.max(
            MIN_NODE_WIDTH,
            (availableWidth - (maxNodesAtLevel - 1) * NODE_HORIZONTAL_SPACING) /
              maxNodesAtLevel
          )
        : NODE_WIDTH;
    const maxChars = Math.max(4, Math.floor(nodeWidth / 8));

    // Position nodes with a placeholder y=0 first. We need the x layout to
    // compute detour lanes (which drive the real top margin), and y is
    // finalized afterwards.
    const nodes: GraphNode[] = [];
    for (let li = 0; li < activeLevels.length; li++) {
      const level = activeLevels[li];
      const group = levelGroups.get(level)!;
      const groupWidth =
        (group.length - 1) * (nodeWidth + NODE_HORIZONTAL_SPACING);
      const startX = (width - groupWidth) / 2;
      for (let gi = 0; gi < group.length; gi++) {
        const t = group[gi];
        const x = startX + gi * (nodeWidth + NODE_HORIZONTAL_SPACING);
        nodes.push({
          id: t.id,
          name: t.name,
          color: t.color || '',
          scope: t.scope,
          level,
          levelIndex: li,
          x,
          y: 0,
        });
      }
    }

    const nodeById = new Map(nodes.map((n) => [n.id, n]));

    // Build links with source color
    const links: GraphLink[] = [];
    for (const t of objectiveTypes) {
      if (t.link_parent_types) {
        for (const parentId of t.link_parent_types) {
          if (nodeById.has(parentId)) {
            links.push({
              sourceId: t.id,
              targetId: parentId,
              sourceColor: t.color || '#95AAC9',
            });
          }
        }
      }
    }

    // Nodes whose right-edge has a self-loop bump; adjacent straight
    // lines can't be used on either side of these without visual overlap.
    const selfLoopedIds = new Set(
      links.filter((l) => l.sourceId === l.targetId).map((l) => l.sourceId)
    );

    // Determine whether a link should render as a straight adjacent line.
    const isStraightAdjacent = (source: GraphNode, target: GraphNode) => {
      if (source.id === target.id) return false;
      if (source.levelIndex !== target.levelIndex) return false;
      const minX = Math.min(source.x, target.x);
      const maxX = Math.max(source.x, target.x);
      const hasNodeBetween = nodes.some(
        (n) =>
          n.levelIndex === source.levelIndex &&
          n.id !== source.id &&
          n.id !== target.id &&
          n.x > minX &&
          n.x < maxX
      );
      const leftNode = source.x < target.x ? source : target;
      return !hasNodeBetween && !selfLoopedIds.has(leftNode.id);
    };

    // Assign a "lane" to each same-level detour link so multiple detours
    // sharing a source/target don't overlap on trunk 1/3, and parallel
    // trunk-2 horizontals don't overlap either. Lanes are per-level and
    // sorted by span (shortest → innermost lane, longest → outermost).
    const detourLaneByLinkIndex = new Map<
      number,
      { lane: number; laneCount: number }
    >();
    for (const level of activeLevels) {
      const levelDetours: { linkIndex: number; span: number }[] = [];
      links.forEach((link, linkIndex) => {
        const source = nodeById.get(link.sourceId);
        const target = nodeById.get(link.targetId);
        if (!source || !target) return;
        if (source.levelIndex !== target.levelIndex) return;
        if (source.level !== level) return;
        if (source.id === target.id) return;
        if (isStraightAdjacent(source, target)) return;
        levelDetours.push({
          linkIndex,
          span: Math.abs(source.x - target.x),
        });
      });
      levelDetours.sort((a, b) => a.span - b.span);
      levelDetours.forEach((d, lane) => {
        detourLaneByLinkIndex.set(d.linkIndex, {
          lane,
          laneCount: levelDetours.length,
        });
      });
    }
    const DETOUR_X_SHIFT = 4; // px separation between trunk 1/3 x-positions

    // Now that lanes are known, size the top margin to the tallest arc.
    const maxLaneCount = Math.max(
      0,
      ...Array.from(detourLaneByLinkIndex.values()).map((v) => v.laneCount)
    );
    const arcTopMargin =
      maxLaneCount > 0
        ? DETOUR_BASE_OFFSET + (maxLaneCount - 1) * DETOUR_STEP_OFFSET + 6
        : 0;
    const topPadding = PADDING_Y + arcTopMargin;

    // Finalize node y positions using the real topPadding.
    for (const n of nodes) {
      n.y =
        topPadding + n.levelIndex * LEVEL_VERTICAL_SPACING + NODE_HEIGHT / 2;
    }

    const totalHeight =
      topPadding +
      PADDING_Y +
      (activeLevels.length - 1) * LEVEL_VERTICAL_SPACING +
      NODE_HEIGHT;

    // Arrow markers per unique color
    const defs = svg.append('defs');
    const uniqueColors = Array.from(new Set(links.map((l) => l.sourceColor)));
    for (const color of uniqueColors) {
      const markerId = `arrowhead-${color.replace('#', '')}`;
      defs
        .append('marker')
        .attr('id', markerId)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 10)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color);
    }

    // Draw links
    svg
      .append('g')
      .selectAll('path')
      .data(links)
      .enter()
      .append('path')
      .attr('d', (d, i) => {
        const sourceNode = nodeById.get(d.sourceId);
        const targetNode = nodeById.get(d.targetId);
        if (!sourceNode || !targetNode) return '';
        const sx = sourceNode.x;
        const sy = sourceNode.y;
        const tx = targetNode.x;
        const ty = targetNode.y;

        // Self-link: a rectangular loop on the right side of the node
        if (d.sourceId === d.targetId) {
          const offset = SELF_LINK_RADIUS;
          const rightEdge = sx + nodeWidth / 2;
          return `M ${rightEdge} ${sy - 8} H ${rightEdge + offset} V ${
            sy + 8
          } H ${rightEdge}`;
        }

        // Same level (same scope, same row).
        if (sourceNode.levelIndex === targetNode.levelIndex) {
          const goingRight = sx < tx;
          if (isStraightAdjacent(sourceNode, targetNode)) {
            const startX = goingRight ? sx + nodeWidth / 2 : sx - nodeWidth / 2;
            const endX = goingRight ? tx - nodeWidth / 2 : tx + nodeWidth / 2;
            return `M ${startX} ${sy} H ${endX}`;
          }

          // Detour — down + across + up for right-going, up + across + down
          // for left-going. The lane (sorted per-level by span, shortest
          // innermost) staggers both the trunk-2 height AND the trunk-1/3
          // x-position so detours sharing a source/target don't overlap.
          const laneInfo = detourLaneByLinkIndex.get(i);
          const lane = laneInfo?.lane ?? 0;
          const anchorY = goingRight
            ? sy + NODE_HEIGHT / 2
            : sy - NODE_HEIGHT / 2;
          const targetAnchorY = goingRight
            ? ty + NODE_HEIGHT / 2
            : ty - NODE_HEIGHT / 2;
          const offset = DETOUR_BASE_OFFSET + lane * DETOUR_STEP_OFFSET;
          const cpY = goingRight
            ? Math.max(anchorY, targetAnchorY) + offset
            : Math.min(anchorY, targetAnchorY) - offset;

          // Shift trunk 1/3 along the node edge by a lane-specific amount
          // so multiple detours sharing the same source (trunk 1) or target
          // (trunk 3) don't collide on their vertical segments.
          const laneShift =
            (lane - (laneInfo?.laneCount ?? 1) / 2 + 0.5) * DETOUR_X_SHIFT;
          const srcX = sx + laneShift;
          const tgtX = tx + laneShift;

          return `M ${srcX} ${anchorY} V ${cpY} H ${tgtX} V ${targetAnchorY}`;
        }

        // Cross-level: bottom of child (source) to top of parent (target)
        const startY = sy - NODE_HEIGHT / 2;
        const endY = ty + NODE_HEIGHT / 2;
        const midY = (startY + endY) / 2;

        return `M ${sx} ${startY} V ${midY} H ${tx} V ${endY}`;
      })
      .attr('fill', 'none')
      .attr('stroke', (d) => d.sourceColor)
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6)
      .attr(
        'marker-end',
        (d) => `url(#arrowhead-${d.sourceColor.replace('#', '')})`
      );

    // Draw nodes
    const nodeGroup = svg
      .append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr(
        'transform',
        (d) => `translate(${d.x - nodeWidth / 2}, ${d.y - NODE_HEIGHT / 2})`
      )
      .style('cursor', onNodeClick ? 'pointer' : null)
      .on('click', (_, d) => onNodeClick?.(d.id));

    nodeGroup
      .append('rect')
      .attr('width', nodeWidth)
      .attr('height', NODE_HEIGHT)
      .attr('rx', NODE_HEIGHT / 2)
      .attr('ry', NODE_HEIGHT / 2)
      .attr('fill', (d) => d.color || '#E3EBF6')
      .attr('stroke', (d) => (d.color ? d.color : '#B1C2D9'))
      .attr('stroke-width', 1);

    nodeGroup
      .append('text')
      .attr('x', nodeWidth / 2)
      .attr('y', NODE_HEIGHT / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .attr('fill', (d) => (d.color ? getContrastColor(d.color) : '#12263F'))
      .text((d) =>
        d.name.length > maxChars
          ? d.name.substring(0, maxChars - 1) + '…'
          : d.name
      );

    // Update SVG height
    d3.select(svgRef.current).attr('height', totalHeight);

    return () => {
      svg.selectAll('*').remove();
    };
  }, [objectiveTypes, svgWidth, onNodeClick]);

  if (objectiveTypes.length < 2) return null;

  return <svg ref={svgRef} width="100%" className="mb-3" />;
};

export default React.memo(ObjectiveTypeLinkGraph);
